import type { TFunction } from 'i18next';

export type SortBy = 'price' | 'alphabetical' | 'updated' | 'featured';
export type SortOrder = 'asc' | 'desc';

/**
 * Get the index name for the given locale, sort by, and order
 * These need to match the index names in the Algolia dashboard.
 * NOTE: There is no descending featured replica; it's just ascending.
 * @param locale The locale of the index (e.g. 'fr'). If not provided, no locale is used.
 * @param sortBy The replica to use (e.g. price, alphabetical, etc.)
 * @param order Ascending or descending order
 */
export const getIndexName = (
  locale: string = 'en',
  sortBy: SortBy = 'updated',
  order: SortOrder = 'desc',
) => {
  let indexName = 'shopify_products';

  if (locale && locale.toLowerCase() !== 'en') {
    indexName += `_canada_${locale.toLowerCase()}`;
  }

  // Updated doesn't add anything to the index name (it's the default)
  if (sortBy !== 'updated') {
    indexName += `_${sortBy}`;
  }

  // Regular sort (none) descending is default (just shopify_products)
  // But regular sort ascending is shopify_products_asc
  if (!(sortBy === 'updated' && order === 'desc')) {
    indexName += `_${order}`;
  }

  return indexName;
};

/** Build out the options for the sortBy widget, for a given locale */
export const buildSortByOptions = (t: TFunction, locale?: string) => {
  return [
    { label: t('algolia:sortBy.featured'),
      value: getIndexName(locale, 'featured', 'asc') },
    { label: t('algolia:sortBy.priceLowToHigh'),
      value: getIndexName(locale, 'price', 'asc') },
    { label: t('algolia:sortBy.priceHighToLow'),
      value: getIndexName(locale, 'price', 'desc') },
    { label: t('algolia:sortBy.newest'),
      value: getIndexName(locale, 'updated', 'desc') },
    { label: t('algolia:sortBy.oldest'),
      value: getIndexName(locale, 'updated', 'asc') },
    { label: t('algolia:sortBy.aToZ'),
      value: getIndexName(locale, 'alphabetical', 'asc') },
    { label: t('algolia:sortBy.zToA'),
      value: getIndexName(locale, 'alphabetical', 'desc') },
  ];
};

/**
 * Parse the named tags from Algolia and return a normalized set of them.
 * It will read named_tags with the locale prefix (e.g. EN_ or FR_) and return
 * the tags without the prefix, ignoring any named_tags that don't match or have a locale.
 * @param namedTags The named tags to filter
 * @param locale The locale to use for filtering
 */
export function getNamedTagsForLocale(namedTags: Record<string, any>, locale: string) {
  // NOTE: Not doing this because the filters sidebar can only filter on a single attribute,
  // so even if we can parse the generic tags, they wouldn't return any results when filtering.

  // Loop once to grab all generic tags
  // const eitherLocaleRegex = new RegExp('^(EN|FR)_(.*)');
  // const noLocaleTags = Object.entries(namedTags).reduce((acc, [key, value]) => {
  //   const eitherLocaleMatch = key.match(eitherLocaleRegex);

  //   // If there are no locale-specific tags, add the generic ones
  //   if (!eitherLocaleMatch) {
  //     acc[key] = value;
  //   }

  //   return acc;
  // }, {} as Record<string, any>);

  // Loop to grab the locale-specific tags
  const desiredLocaleRegex = new RegExp(`^${locale.toUpperCase()}_(.*)`);
  const workingCopy = Object.entries(namedTags).reduce((acc, [key, value]) => {
    const desiredLocaleMatch = key.match(desiredLocaleRegex);

    // If tag is in the desired locale, add it to the working copy
    if (desiredLocaleMatch) {
      const tagName = desiredLocaleMatch[1];
      acc[tagName] = value;
    }

    return acc;
  // }, noLocaleTags);
  }, {} as Record<string, any>);

  return workingCopy;
};
